import { Download, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';

import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import type { MenuItem } from '../../../../components/Menu';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ApiError } from '../../../../lib/apiError';
import {
  fetchIssuanceForm,
  issuanceFormDisabledReason,
  issuanceFormLabel,
  issuanceFormTitle,
  printIssuanceForm,
  saveIssuanceForm,
} from '../issuanceForm';
import type { IssuanceDetail, PropertyClassification } from '../issuanceTypes';

/**
 * The slip's ICS or PAR — the profile's Preview tab.
 *
 * **A `<ProfileCard>` like every other section on the page**, inset on the panel's own `p-4`
 * rather than run flush to its edges, titled with the form's printed name. The document is the
 * card's body, stretched to the height the panel offers (`fill`), so the browser's viewer gets
 * a page to draw on rather than its 150px default.
 *
 * **Download and Print are the card's COG**, the place a section's actions live — never bare
 * buttons, and not repeated in the header's cog, which would be one action in two places on one
 * screen. They act on the bytes already on screen rather than drawing the form a second time.
 *
 * **Fetched on MOUNT, and mounted only while its tab is open** — the form is drawn on the server
 * per request, so opening the profile must not draw one for a reader who never looks at it.
 *
 * **The frame shows a blob re-typed to `application/pdf`** (`issuanceForm.ts`), never the
 * endpoint itself — which serves an attachment, and which a frame could not authenticate to.
 */
export function IssuanceFormPreview({
  issuance,
  form,
}: {
  issuance: IssuanceDetail;
  form: PropertyClassification;
}) {
  const label = issuanceFormLabel(form);
  const blocked = issuanceFormDisabledReason(issuance);
  const fallbackName = `${label}-${issuance.controlNumber ?? issuance.issuanceNumber}.pdf`;

  const [file, setFile] = useState<{ blob: Blob; fileName: string } | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!blocked);
  const [error, setError] = useState<ApiError | null>(null);

  // Revoked when it is REPLACED, after the render carrying the new one has committed.
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  useEffect(() => {
    if (blocked) return;

    /*
      No loading/error reset here: the panel is KEYED on the issuance by its caller, so a
      different slip is a fresh mount with fresh state, and this runs once per mount.
    */
    const controller = new AbortController();

    fetchIssuanceForm(issuance.id, controller.signal)
      .then(({ blob, fileName }) => {
        if (controller.signal.aborted) return;

        setFile({ blob, fileName: fileName ?? fallbackName });
        setUrl(URL.createObjectURL(blob));
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setUrl(null);
        setError(caught instanceof ApiError ? caught : new ApiError(`Could not load the ${label}.`, 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [blocked, issuance.id, label, fallbackName]);

  /*
    Disabled for STATE with the reason — a deleted slip, or a form still drawing or that failed
    to — never hidden, because the reader holds the key and the verbs are still this card's.
  */
  const unavailable =
    blocked ?? (loading ? `The ${label} is still loading.` : file ? null : `The ${label} could not be loaded.`);

  const menuItems: MenuItem[] = [
    {
      key: 'issuance-form-download',
      label: `Download ${label}`,
      icon: <Download className="h-4 w-4" aria-hidden="true" />,
      permission: 'issuances.read',
      disabledReason: unavailable,
      onClick: () => {
        if (file) saveIssuanceForm(file.blob, file.fileName);
      },
    },
    {
      key: 'issuance-form-print',
      label: `Print ${label}`,
      icon: <Printer className="h-4 w-4" aria-hidden="true" />,
      permission: 'issuances.read',
      disabledReason: unavailable,
      onClick: () => {
        if (file) printIssuanceForm(file.blob);
      },
    },
  ];

  return (
    // The inset a `bleed` panel owes its child — the card stands on the panel, not against it.
    <div className="flex h-full min-h-0 flex-col p-4">
      <ProfileCard id="issuance-profile-form" title={issuanceFormTitle(form)} menuItems={menuItems} fill>
        {blocked ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
            <EmptyState message={blocked} />
          </div>
        ) : loading ? (
          // One tall block — a page of document, not a list arriving.
          <Skeleton className="m-4 min-h-[420px] flex-1" />
        ) : error || !url ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
            <ErrorState
              message={error?.message ?? `Could not load the ${label}.`}
              correlationId={error?.correlationId}
            />
          </div>
        ) : (
          <iframe
            src={url}
            title={`${label} ${issuance.controlNumber ?? issuance.issuanceNumber}`}
            className="min-h-[420px] w-full flex-1 border-0 bg-slate-50"
          />
        )}
      </ProfileCard>
    </div>
  );
}
